/**
 * Google Vision API OCR Service
 * Direct integration with Google Vision API for text extraction from certificates
 */

const GOOGLE_VISION_API_KEY = process.env.REACT_APP_GOOGLE_VISION_API_KEY || "AIzaSyDQFKt4QNJV2Em45MGJTouEXOaWlvqsMj8";

/**
 * Google Vision OCR Service Class
 * Provides certificate data extraction compatible with authentication service
 */
class GoogleVisionOCRService {
  /**
   * Extract certificate data from image using Google Vision API
   * @param {File} file - Certificate image file
   * @returns {Promise<Object>} Extracted certificate data
   */
  async extractCertificateData(file) {
    try {
      console.log('📄 Extracting certificate data with Google Vision...');
      
      // Extract text using Google Vision
      const extractedText = await extractTextWithGoogleVision(file);
      
      // Parse the extracted text into structured data
      const data = this.parseCertificateText(extractedText);
      
      console.log('✅ Certificate data extracted:', data);
      return data;
      
    } catch (error) {
      console.error('❌ Failed to extract certificate data:', error);
      throw error;
    }
  }

  /**
   * Parse extracted text into structured certificate data
   * @param {string} text - Raw extracted text
   * @returns {Object} Structured certificate data
   */
  parseCertificateText(text) {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    
    // Initialize data object
    const data = {
      institution: '',
      student_name: '',
      certificate_type: '',
      program: '',
      issue_date: '',
      grade: '',
      certificate_number: '',
      raw_text: text
    };

    // Common patterns
    const datePattern = /\b(\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}|\d{4}[-\/]\d{1,2}[-\/]\d{1,2}|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2},?\s+\d{4})/i;
    const gradePattern = /\b([A-F][+-]?|[0-9]{1,3}(?:\.[0-9]{1,2})?%?)\b/;
    const certNumberPattern = /\b(?:certificate|cert|id|no\.?|number|#)\s*:?\s*([A-Z0-9-]+)\b/i;

    // Extract institution (usually in first few lines or has keywords)
    const institutionKeywords = ['university', 'college', 'institute', 'school', 'academy'];
    for (const line of lines.slice(0, 5)) {
      if (institutionKeywords.some(keyword => line.toLowerCase().includes(keyword))) {
        data.institution = line;
        break;
      }
    }

    // Extract certificate type
    const certTypeKeywords = ['certificate', 'diploma', 'degree', 'bachelor', 'master', 'phd'];
    for (const line of lines) {
      if (certTypeKeywords.some(keyword => line.toLowerCase().includes(keyword))) {
        data.certificate_type = line;
        break;
      }
    }

    // Extract dates
    for (const line of lines) {
      const dateMatch = line.match(datePattern);
      if (dateMatch && !data.issue_date) {
        data.issue_date = dateMatch[0];
        break;
      }
    }

    // Extract certificate number
    for (const line of lines) {
      const certMatch = line.match(certNumberPattern);
      if (certMatch) {
        data.certificate_number = certMatch[1];
        break;
      }
    }

    // Extract grade
    for (const line of lines) {
      const gradeMatch = line.match(gradePattern);
      if (gradeMatch) {
        data.grade = gradeMatch[0];
        break;
      }
    }

    // Extract student name (heuristic: look for "name" keyword or capitalized name patterns)
    const namePattern = /(?:name|student|recipient)\s*:?\s*([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/i;
    for (const line of lines) {
      const nameMatch = line.match(namePattern);
      if (nameMatch) {
        data.student_name = nameMatch[1];
        break;
      }
      // Fallback: look for lines with multiple capitalized words
      if (!data.student_name && /^[A-Z][a-z]+\s+[A-Z][a-z]+/.test(line)) {
        data.student_name = line;
      }
    }

    // Extract program/course (look for degree program names)
    const programKeywords = ['science', 'arts', 'engineering', 'business', 'technology', 'medicine'];
    for (const line of lines) {
      if (programKeywords.some(keyword => line.toLowerCase().includes(keyword)) && !data.program) {
        data.program = line;
        break;
      }
    }

    return data;
  }

  /**
   * Test Google Vision API connection
   * @returns {Promise<Object>} Connection status
   */
  async testConnection() {
    return await testGoogleVisionAPI();
  }
}

// Export singleton instance
export const googleVisionOCR = new GoogleVisionOCRService();

/**
 * Convert file to base64 string
 * @param {File} file - The image file to convert
 * @returns {Promise<string>} Base64 encoded string
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data:image/jpeg;base64, prefix
      const base64String = reader.result.split(',')[1];
      resolve(base64String);
    };
    reader.onerror = error => reject(error);
  });
};

/**
 * Extract text from image using Google Vision API
 * @param {File} file - The image file to process
 * @returns {Promise<string>} Extracted text
 */
export const extractTextWithGoogleVision = async (file) => {
  try {
    console.log('🔍 Starting Google Vision API OCR...');
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('Only image files are supported for Google Vision API');
    }

    // Convert file to base64
    const base64Content = await fileToBase64(file);
    
    // Prepare request payload
    const requestPayload = {
      requests: [{
        image: {
          content: base64Content
        },
        features: [{
          type: 'TEXT_DETECTION',
          maxResults: 1
        }]
      }]
    };

    // Make API request
    const response = await fetch(`https://vision.googleapis.com/v1/images:annotate?key=${GOOGLE_VISION_API_KEY}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestPayload)
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Google Vision API Error:', errorData);
      throw new Error(`Google Vision API Error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
    }

    const result = await response.json();
    console.log('✅ Google Vision API Response:', result);

    // Extract text from response
    if (result.responses && result.responses[0]) {
      const textAnnotations = result.responses[0].textAnnotations;
      if (textAnnotations && textAnnotations.length > 0) {
        const extractedText = textAnnotations[0].description;
        console.log('📝 Extracted Text:', extractedText);
        return extractedText;
      }
    }

    console.warn('⚠️ No text found in image');
    return '';

  } catch (error) {
    console.error('❌ OCR Error:', error);
    throw error;
  }
};

/**
 * Test Google Vision API connectivity
 * @returns {Promise<Object>} Test result
 */
export const testGoogleVisionAPI = async () => {
  try {
    // Create a simple test image data (1x1 white pixel PNG)
    const testImageBase64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==';
    
    const requestPayload = {
      requests: [{
        image: {
          content: testImageBase64
        },
        features: [{
          type: 'TEXT_DETECTION',
          maxResults: 1
        }]
      }]
    };

    const response = await fetch(`https://vision.googleapis.com/v1/images:annotate?key=${GOOGLE_VISION_API_KEY}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestPayload)
    });

    const result = await response.json();

    if (response.ok) {
      return {
        success: true,
        status: 'success',
        message: '✅ Google Vision API is working correctly!',
        apiKey: `${GOOGLE_VISION_API_KEY.substring(0, 12)}...${GOOGLE_VISION_API_KEY.substring(GOOGLE_VISION_API_KEY.length - 6)}`,
        timestamp: new Date().toISOString(),
        responseTime: `${Date.now() - Date.now()}ms`
      };
    } else {
      return {
        success: false,
        status: 'error',
        message: `❌ Google Vision API Error: ${result.error?.message || 'Unknown error'}`,
        error: result.error
      };
    }
  } catch (error) {
    return {
      success: false,
      status: 'error',
      message: `❌ Connection Error: ${error.message}`,
      error: error.message
    };
  }
};

export default extractTextWithGoogleVision;